import type { FC } from "react"
import React, { memo, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import styled from "styled-components"

import SimpleLoader from "~/components/SimpleLoader"
import {
  shownNetSavings,
  shownProposalSavingsCompute,
} from "~/containers/Proposal/state/savings/proposal-savings-compute"
import useProposalSavings from "~/containers/Proposal/state/savings/use-proposal-savings"
import { formatInteger } from "~/utils/finance/format"

import { Dial } from "./Dial"
import { Disclaimers } from "./Disclaimers"
import { HouseHero } from "./HouseHero"
import { monthlyBillSlideReadyState } from "./monthly-bill-slide-ready-state"
import { AverageUtilityBill, SolarPayment } from "./PaymentSections"
import { getDialRateToDisplay } from "./rate-display"
import { Stats } from "./Stats"
import { DEVICE } from "../../../../constants/device-widths"
import { brochureState } from "../../state/brochure/brochure-state"
import { poisonedDesignState } from "../../state/designs/design-state"
import { useProposalContext } from "../../state/proposal/proposal-context"
import {
  syncWaitingForEntityState,
  utilitySavingsPendingState,
} from "../../state/sync-state"
import { calculatedUtilityRateState } from "../../state/utility-costs-and-savings/utility-costs-and-savings-state"
import { Inner } from "../brochure-styles"

const OuterWrapper = styled(Inner)`
  justify-content: flex-start;
  align-items: center;
  max-width: 1920px;
  padding: 50px 0;
  min-height: fit-content;
  @media print {
    min-height: 100vh;
  }
`
const InnerWrapper = styled(Inner)`
  padding: 0 clamp(15px, 3vw, 100px);
  min-height: 0;
  min-height: fit-content;
`
const PlaceholderForFullScreen = styled.div`
  width: 100%;
  aspect-ratio: 3/1;
  min-height: 170px;
  z-index: 0;
  pointer-events: none;
  position: relative;
  display: block;
  @media ${DEVICE.desktop} {
    display: none;
  }
`
const MiddleSection = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  justify-content: center;
  gap: 15vw;
  @media ${DEVICE.tablet} {
    gap: 0;
  }
`
const DialSection = styled.div`
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  width: 100%;
  @media ${DEVICE.tablet} {
    max-width: 450px;
    position: relative;
  }
  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-width: 320px;
    position: absolute;
    @media ${DEVICE.tablet} {
      width: 100%;
      height: 100%;
      transform: translateY(clamp(-200px, -10vw, 0px));
    }
  }
`
const DialWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`
const DialInnerText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  transform: translateY(23%);
  h2 {
    font-size: var(--step-3);
    font-weight: 600;
    color: var(--blue-dark-font);
  }
  h3 {
    font-size: var(--step-1);
    font-weight: 500;
    color: var(--blue);
  }
  h4 {
    margin-top: 5%;
    font-size: var(--step--1);
  }
`
const DialSubText = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 10;
  width: 100%;
  transform: translateY(-20%);
  > p {
    font-size: var(--step-0);
    font-weight: 500;
    color: var(--blue);
    letter-spacing: 1px;
  }
  > div {
    background: linear-gradient(180deg, #e7f3fa 0%, #ffffff 100%);
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.14);
    border-radius: 6px;
    width: 60%;
    aspect-ratio: 4/1;
    display: flex;
    justify-content: center;
    color: var(--blue);
    align-items: center;
    letter-spacing: 1px;
    h3 {
      font-size: var(--step-2);
    }
  }
`

const SubText: FC = () => {
  const { t } = useTranslation()
  const proposal = useProposalContext()
  const { data: savings, isError: isSavingsError } = useProposalSavings(
    proposal.local.id,
  )
  const netSavings = shownNetSavings(savings)
  const includesCompute = !!shownProposalSavingsCompute(savings)
  const utilitySavingsPending = useRecoilValue(utilitySavingsPendingState)
  const poisonedDesign = useRecoilValue(poisonedDesignState)
  const isSavingsPending = savings === undefined && !isSavingsError

  let message = <h3>{`$` + formatInteger(netSavings)}</h3>

  if (poisonedDesign) {
    message = (
      <h3
        style={{
          color: `var(--danger)`,
        }}
      >
        N/A
      </h3>
    )
  }

  if (utilitySavingsPending || isSavingsPending) {
    message = <SimpleLoader />
  }

  return (
    <DialSubText>
      <p>{t(`dialSavings`)}</p>
      <div>{message}</div>
      {includesCompute && !poisonedDesign && (
        <span className="text-muted block w-3/5 text-center text-xs">
          {t(`monthlyBillComputeNote`)}
        </span>
      )}
    </DialSubText>
  )
}

export const dialYearState = atom<{ years: number }>({
  key: `dialYear`,
  default: {
    years: 1,
  },
})

const min = 90

export const degreesToYear = (degrees: number, increment: number): number => {
  const startPoint = degrees - min
  const year = startPoint / increment + 1

  return Math.round(year)
}

/**
 * Converts a given year into a degree value based on an incremental step.
 */
export const yearToDegrees = (year: number, increment: number): number => {
  return min + (year - 1) * increment
}

/**
 * A 30-rate dial uses a tighter step so all 30 years fit in the 90-270 degree
 * sweep; anything else keeps the default 25-year step.
 */
export const getDialIncrement = (dialUtilityRatesCount: number): number =>
  dialUtilityRatesCount === 30 ? 6.2 : 7.5

export const MonthlyBillInternal: FC<{
  navRef: React.RefObject<HTMLHeadingElement>
}> = ({ navRef }) => {
  const { t } = useTranslation()
  const isBrochureLoading = useRecoilValue(syncWaitingForEntityState(`brochure`))
  const [dial, setDial] = useRecoilState(dialYearState)
  const [isDialReady, setIsDialReady] = useState(false)
  const setMonthlyBillSlideReady = useSetRecoilState(monthlyBillSlideReadyState)

  const { search } = useLocation()

  // Sync the dial with the URL parameter when present (covers initial load
  // and back/forward navigation).
  useEffect(() => {
    const query = new URLSearchParams(search)
    const yearsQuery = query.get(`years`)
    if (!yearsQuery) return

    const MIN_DIAL_YEARS = 1
    const MAX_DIAL_YEARS = 30

    const years = Number(yearsQuery)
    const hasInvalidYears =
      !Number.isInteger(years) ||
      years < MIN_DIAL_YEARS ||
      years > MAX_DIAL_YEARS

    if (hasInvalidYears) {
      return
    }

    setDial({ years })
  }, [search, setDial])

  const utilitySavingsPending = useRecoilValue(utilitySavingsPendingState)
  const { dialUtilityRates } = useRecoilValue(brochureState)
  const calculatedUtilityRate = useRecoilValue(calculatedUtilityRateState)
  const proposal = useProposalContext()
  const { data: savings, isError: isSavingsError } = useProposalSavings(
    proposal.local.id,
  )
  const isSavingsPending = savings === undefined && !isSavingsError

  // Derived during render: the realistic-drag dial only ever produces degree
  // values quantized to the increment, so years <-> degrees round-trips
  // exactly and neither needs to live in state.
  const increment = getDialIncrement(dialUtilityRates.length)
  const degrees = yearToDegrees(dial.years, increment)

  const updateYears = (newDegrees: number) => {
    setDial({ years: degreesToYear(newDegrees, increment) })
  }

  const rateToBeDisplayed = getDialRateToDisplay({
    dialYear: dial.years,
    calculatedUtilityRate,
    dialUtilityRates,
  })

  // Report readiness when the dial and brochure have finished loading real data.
  useEffect(() => {
    const isReady =
      isDialReady &&
      !utilitySavingsPending &&
      !isSavingsPending &&
      !isBrochureLoading
    setMonthlyBillSlideReady(isReady)

    // Reset readiness when component unmounts
    return () => {
      setMonthlyBillSlideReady(false)
    }
  }, [
    isDialReady,
    utilitySavingsPending,
    isSavingsPending,
    isBrochureLoading,
    setMonthlyBillSlideReady,
  ])

  return (
    <OuterWrapper>
      <PlaceholderForFullScreen />
      <HouseHero navRef={navRef} />
      <InnerWrapper>
        <MiddleSection>
          <AverageUtilityBill />
          <DialSection>
            <div>
              <DialWrapper>
                <Dial
                  degrees={degrees}
                  setDegrees={updateYears}
                  mustUseHandle={false}
                  increment={increment}
                  realisticDrag={true}
                  min={min}
                  max={270}
                  initial={90}
                  SubText={<SubText />}
                  onReady={() => setIsDialReady(true)}
                >
                  <DialInnerText>
                    <h2>{dial.years}</h2>
                    <h3>{t(dial.years === 1 ? `dialYear` : `dialYears`)}</h3>
                    {!utilitySavingsPending ? (
                      <h4>${rateToBeDisplayed.toFixed(2)}/kWh</h4>
                    ) : (
                      <SimpleLoader />
                    )}
                  </DialInnerText>
                </Dial>
              </DialWrapper>
            </div>
          </DialSection>
          <SolarPayment />
        </MiddleSection>
        <Stats />
        <Disclaimers />
      </InnerWrapper>
    </OuterWrapper>
  )
}

export const MonthlyBill = memo(MonthlyBillInternal)
